import type { Middleware } from '@reduxjs/toolkit';
import { loadNotifications } from '../features/notifications/notificationsSlice';
import type { NotificationItem } from '../api/types';
import { loadUserShares } from '../features/sharing/sharingSlice';
import { loadGroups } from '../features/sharing/groupsSlice';
import {
  loadReceivedRequests,
  loadSentRequests,
} from '../features/borrowRequests/borrowRequestsSlice';
import { loadInventoryItemsEnhanced } from '../features/browse/browseSlice';

/**
 * FE-255 PR6 — notification-triggered slice invalidation middleware.
 *
 * The optimistic-UI work in PRs 1–4 hides the lag for things the user
 * dispatched on THIS device. PR5's `useRefetchOnFocus` closes the gap
 * for screens the user navigates back to. This middleware closes the
 * third gap: data that changes while the user is sitting on an
 * unrelated screen, where focus refetch won't help because the user
 * never leaves and returns.
 *
 * The signal we use: NEW notifications arriving via the existing
 * `loadNotifications` thunk. The notifications endpoint is already
 * polled (NotificationBell ticks `refreshUnreadCount` every 60s and
 * `useRefetchOnFocus` on Dashboard / ShareFlow fires `loadNotifications`
 * on every screen entry). When fresh rows land, we diff them against
 * the previously-known set in `state.notifications.byId` and dispatch
 * the right invalidation thunk per notification type.
 *
 * Why not listen for `bumpUnreadCount` from `refreshUnreadCount`?
 * Because that path only carries a number — we don't know what type
 * of notification arrived, so we can't target the right slice. By
 * the time `loadNotifications.fulfilled` runs we have the full row
 * with `.type`, which is the dimension we need.
 *
 * ## Behavior
 *
 * - Listens for `loadNotifications.fulfilled`.
 * - Snapshots `byId` keys BEFORE the reducer runs (the previously
 *   known set).
 * - After `next(action)`, identifies notifications in the payload
 *   whose id was NOT in the snapshot — i.e. genuinely new this poll.
 * - Cold-start guard: if the snapshot was empty (first
 *   `loadNotifications` of the session), skip the cascade entirely.
 *   On cold start every screen's mount-time `useEffect` already
 *   fires its own load; the middleware would just duplicate that.
 * - For each new notif, accumulates the set of refresh thunks to
 *   dispatch (deduped — N HANDOFFs in one batch still only fires
 *   one inventory refresh).
 * - Informational types (`CUSTODY_REMINDER`, `OVERDUE`,
 *   `CUSTODY_RECALL_ACK`) trigger NO refresh — they don't change
 *   any cached state the FE renders from list endpoints. Their
 *   detail surface (item detail) will refresh on focus.
 *
 * ## What this does NOT do
 *
 * - It does not refresh per-item caches (custody history for a
 *   single itemId). The list-level refresh covers the visible
 *   surfaces; item detail uses `useRefetchOnFocus` (PR5).
 * - It does not debounce internally — Redux Toolkit thunks already
 *   abort previous in-flight requests for the same dispatch chain,
 *   so duplicate `loadInventoryItemsEnhanced` calls within ms
 *   coalesce naturally. We DO dedupe within a single fulfilled
 *   action's worth of notifications via the `thunksToFire` set.
 * - It is unaware of `useRefetchOnFocus`. If the user is on a
 *   focused screen, the focus refetch may also fire — and they'll
 *   coalesce at the thunk layer. Cheaper than threading focus
 *   state into the middleware.
 */

const INFORMATIONAL: ReadonlySet<string> = new Set<string>([
  'CUSTODY_REMINDER',
  'CUSTODY_RECALL_ACK',
  'OVERDUE',
  'WAITLIST_UPDATE',
]);

/**
 * Map a notification type to the set of slice-refresh "buckets" it
 * invalidates. Buckets exist because multiple notif types invalidate
 * the same slice (e.g. SHARE_RECEIVED / SHARE_CLAIMED both touch
 * `/users/shares`) — bucketing dedupes one call rather than one per
 * notif.
 *
 * Bucket names ARE the export name of the thunk they map to (below);
 * the switch keeps the symbols out of this lookup so the bundler can
 * tree-shake unused thunks if the slices migrate in the future.
 */
type InvalidationBucket =
  | 'shares'
  | 'groups'
  | 'received-requests'
  | 'sent-requests'
  | 'inventory';

function bucketsForType(t: string | undefined): InvalidationBucket[] {
  switch (t) {
    case 'SHARE_RECEIVED':
    case 'SHARE_CLAIMED':
    case 'SHARE_CANCELLED_BY_OWNER':
      return ['shares'];

    case 'GROUP_INVITE_RECEIVED':
    case 'GROUP_INVITE_ACCEPTED':
    case 'GROUP_INVITE_DECLINED':
    case 'CREW_INVITE_RECEIVED':
    case 'CREW_INVITE_ACCEPTED':
    case 'CREW_INVITE_DECLINED':
      return ['groups'];

    case 'BORROW_REQUEST_RECEIVED':
      return ['received-requests'];

    case 'BORROW_APPROVED':
    case 'BORROW_DECLINED':
      return ['sent-requests'];

    case 'CUSTODY_HANDOFF':
    case 'CUSTODY_RETURNED':
    case 'CUSTODY_RECALL':
      // Both the holder and the owner need a fresh inventory list:
      // the holder's `current_custodian_user_id` flipped, the owner's
      // chip flips correspondingly. Single thunk covers both.
      return ['inventory'];

    default:
      // Informational types and unknown types: no list-level refresh.
      return [];
  }
}

/**
 * Pure helper exported for the test harness — given a snapshot of
 * previously-known notification ids and the freshly fetched list,
 * compute which invalidation buckets to fire. Returning an array
 * (in deterministic order, sorted) makes assertions trivial.
 */
export function computeBucketsToInvalidate(
  prevIds: ReadonlySet<number>,
  fetched: ReadonlyArray<Pick<NotificationItem, 'id' | 'type'>>,
): InvalidationBucket[] {
  // Cold-start guard — see middleware doc above.
  if (prevIds.size === 0) return [];

  const buckets = new Set<InvalidationBucket>();
  for (const n of fetched) {
    if (prevIds.has(n.id)) continue;
    if (INFORMATIONAL.has(String(n.type))) continue;
    for (const b of bucketsForType(n.type as string)) {
      buckets.add(b);
    }
  }
  // Stable order — easier to test, no observable difference in prod.
  const order: InvalidationBucket[] = [
    'shares',
    'groups',
    'received-requests',
    'sent-requests',
    'inventory',
  ];
  return order.filter((b) => buckets.has(b));
}

export const notificationInvalidationMiddleware: Middleware =
  (storeAPI) => (next) => (action) => {
    if (!loadNotifications.fulfilled.match(action)) {
      return next(action);
    }

    // Snapshot the previously-known ids BEFORE the reducer merges
    // the new payload into `byId`.
    const prevState = storeAPI.getState() as any;
    const prevById = prevState?.notifications?.byId ?? {};
    const prevIds = new Set<number>(
      Object.keys(prevById).map((k) => Number(k)),
    );

    const result = next(action);

    const fetched = action.payload?.data?.notifications ?? [];
    const buckets = computeBucketsToInvalidate(prevIds, fetched);

    for (const b of buckets) {
      switch (b) {
        case 'shares':
          (storeAPI.dispatch as any)(loadUserShares({} as any));
          break;
        case 'groups':
          (storeAPI.dispatch as any)(loadGroups());
          break;
        case 'received-requests':
          (storeAPI.dispatch as any)(loadReceivedRequests());
          break;
        case 'sent-requests':
          (storeAPI.dispatch as any)(loadSentRequests());
          break;
        case 'inventory':
          (storeAPI.dispatch as any)(
            loadInventoryItemsEnhanced({ refresh: true }),
          );
          break;
      }
    }

    return result;
  };
